/* eslint-disable prettier/prettier */

import {
  tw2018func_cloneObj,
  tw2018func_getPageSizeInches,
  tw2018func_getPDFFileNameFromURL,
  tw2018func_isPortraitOrientation,
} from "./util.js";

import { createPromiseCapability } from "./pdfjs-lib.js";
const DEFAULT_FIELD_CONTENT = "-";

// Should use the format: `width x height`, in portrait orientation.
// See https://en.wikipedia.org/wiki/Paper_size
const US_PAGE_NAMES = {
  "8.5x11": "Letter",
  "8.5x14": "Legal",
};
const METRIC_PAGE_NAMES = {
  "297x420": "A3",
  "210x297": "A4",
};

function tw2018func_getPageName(size, isPortrait, pageNames) {
  const width = isPortrait ? size.width : size.height;
  const height = isPortrait ? size.height : size.width;

  return pageNames[`${width}x${height}`];
}

/**
 * @typedef {Object} PDFDocumentPropertiesOptions
 * @property {string} overlayName - Name/identifier for the overlay.
 * @property {Object} fields - Names and elements of the overlay's fields.
 * @property {HTMLDivElement} container - Div container for the overlay.
 * @property {HTMLButtonElement} closeButton - Button for closing the overlay.
 * @ignore
 */

class PDFDocumentProperties {
  /**
   * @param {PDFDocumentPropertiesOptions} options
   * @param {EventBus} eventBus - The application event bus.
   * @param {IL10n} l10n - Localization service.
   * @ignore
   */
  constructor(
    { app, overlayName, fields, container, closeButton },
    eventBus,
    l10n
  ) {
    this._app = app;
    this.overlayName = overlayName;
    this.fields = fields;
    this.container = container;
    this.l10n = l10n;
    this._reset();
    this._dataAvailableCapability = createPromiseCapability();
    if (closeButton) {
      // Bind the event listener for the Close button.
      closeButton.addEventListener("click", this.close.bind(this));
    }
    this._app.overlayManager.register(
      this.overlayName,
      this.container,
      this.close.bind(this),
      true
    );

    if (eventBus) {
      eventBus.on("pagechanging", evt => {
        this._currentPageNumber = evt.pageNumber;
      });
      eventBus.on("rotationchanging", evt => {
        this._pagesRotation = evt.pagesRotation;
      });
    }

    this._isNonMetricLocale = true; // The default viewer locale is 'en-us'.
    // TODO
    /* l10n.getLanguage().then(locale => {
      this._isNonMetricLocale = NON_METRIC_LOCALES.includes(locale);
    });
    */
  }

  /**
   * Open the document properties overlay.
   */
  open() {
    const freezeFieldData = data => {
      console.log(data);
      Object.defineProperty(this, "fieldData", {
        value: Object.freeze(data),
        writable: false,
        enumerable: true,
        configurable: true,
      });
    };

    Promise.all([
      this._app.overlayManager.open(this.overlayName),
      // , this._dataAvailableCapability.promise
    ])
      .then(() => {
        const currentPageNumber = this._currentPageNumber;
        const pagesRotation = this._pagesRotation;

        // If the document properties were previously fetched
        // (for this PDF file), just update the dialog immediately
        // to avoid redundant lookups.
        if (
          this.fieldData &&
          currentPageNumber === this.fieldData._currentPageNumber &&
          pagesRotation === this.fieldData._pagesRotation
        ) {
          this._updateUI();
          return;
        }

        // Get the document properties.
        this.pdfDocument
          .getMetadata()
          .then(({ info, metadata, contentDispositionFilename }) => {
            return Promise.all([
              info,
              metadata,
              contentDispositionFilename ||
                tw2018func_getPDFFileNameFromURL(this.url),
              this._parseFileSize(this.maybeFileSize),
              this._parseDate(info.CreationDate),
              this._parseDate(info.ModDate),
              this.pdfDocument.getPage(currentPageNumber).then(pdfPage => {
                return this._parsePageSize(
                  tw2018func_getPageSizeInches(pdfPage),
                  pagesRotation
                );
              }),
            ]);
          })
          .then(
            ([
              info,
              metadata,
              fileName,
              fileSize,
              creationDate,
              modDate,
              pageSize,
            ]) => {
              freezeFieldData({
                fileName,
                fileSize,
                title: info.Title,
                author: info.Author,
                subject: info.Subject,
                keywords: info.Keywords,
                creationDate,
                modificationDate: modDate,
                creator: info.Creator,
                producer: info.Producer,
                version: info.PDFFormatVersion,
                pageCount: this.pdfDocument.numPages,
                pageSize,
                _currentPageNumber: currentPageNumber,
                _pagesRotation: pagesRotation,
              });
              this._updateUI();

              // Get the correct fileSize, since it may not have been set (if
              // `this.setFileSize` wasn't called) or may be incorrectly set.
              return this.pdfDocument.getDownloadInfo();
            }
          )
          .then(({ length }) => {
            this.maybeFileSize = length;
            return this._parseFileSize(length);
          })
          .then(fileSize => {
            if (fileSize === this.fieldData.fileSize) {
              return; // The fileSize has already been correctly set.
            }
            const data = tw2018func_cloneObj(this.fieldData);
            data.fileSize = fileSize;

            freezeFieldData(data);
            this._updateUI();
          });
      })
      .catch(function (error) {
        console.log(error);
      });
  }

  /**
   * Close the document properties overlay.
   */
  close() {
    this._app.overlayManager.close(this.overlayName);
  }

  /**
   * Set a reference to the PDF document and the URL in order
   * to populate the overlay fields with the document properties.
   * Note that the overlay will contain no information if this method
   * is not called.
   *
   * @param {Object} pdfDocument - A reference to the PDF document.
   * @param {string} url - The URL of the document.
   */
  setDocument(pdfDocument, url) {
    if (this.pdfDocument) {
      this._reset();
      this._updateUI(true);
    }
    if (!pdfDocument) {
      return;
    }
    this.pdfDocument = pdfDocument;
    this.url = url;

    this._dataAvailableCapability.resolve();
  }

  /**
   * Set the file size of the PDF document. This method is used to
   * update the file size in the document properties overlay once it
   * is known so we do not have to wait until the entire file is loaded.
   *
   * @param {number} fileSize - The file size of the PDF document.
   */
  setFileSize(fileSize) {
    if (Number.isInteger(fileSize) && fileSize > 0) {
      this.maybeFileSize = fileSize;
    }
  }

  /**
   * @private
   */
  _reset() {
    this.pdfDocument = null;
    this.url = null;

    this.maybeFileSize = 0;
    delete this.fieldData;
    this._dataAvailableCapability = createPromiseCapability();
    this._currentPageNumber = 1;
    this._pagesRotation = 0;
  }

  /**
   * Always updates all of the dialog fields, to prevent inconsistent UI state.
   * NOTE: If the contents of a particular field is neither a non-empty string,
   *       nor a number, it will fall back to `DEFAULT_FIELD_CONTENT`.
   * @private
   */
  _updateUI(reset = false) {
    if (reset || !this.fieldData) {
      for (const id in this.fields) {
        this.fields[id].textContent = DEFAULT_FIELD_CONTENT;
      }
      return;
    }
    if (this._app.overlayManager.active !== this.overlayName) {
      // Don't bother updating the dialog if has already been closed,
      // since it will be updated the next time `this.open` is called.
      return;
    }
    for (const id in this.fields) {
      const content = this.fieldData[id];
      this.fields[id].textContent =
        content || content === 0 ? content : DEFAULT_FIELD_CONTENT;
    }
  }

  /**
   * @private
   */
  _parseFileSize(fileSize = 0) {
    const kb = fileSize / 1024;
    if (!kb) {
      return Promise.resolve(undefined);
    }

    const size_b = fileSize.toLocaleString();
    if (kb < 1024) {
      const size_kb = (+kb.toPrecision(3)).toLocaleString();
      return `${size_kb} KB (${size_b} bytes)`;
    }

    const size_mb = (+(kb / 1024).toPrecision(3)).toLocaleString();
    return `${size_mb} MB (${size_b} bytes)`;
  }

  /**
   * @private
   */
  _parsePageSize(pageSizeInches, pagesRotation) {
    if (!pageSizeInches) {
      return Promise.resolve(undefined);
    }
    // Take the viewer rotation into account as well; compare with Adobe Reader.
    if (pagesRotation % 180 !== 0) {
      pageSizeInches = {
        width: pageSizeInches.height,
        height: pageSizeInches.width,
      };
    }
    const isPortrait = tw2018func_isPortraitOrientation(pageSizeInches);

    let sizeInches = {
      width: Math.round(pageSizeInches.width * 100) / 100,
      height: Math.round(pageSizeInches.height * 100) / 100,
    };
    // 1in == 25.4mm; no need to round to 2 decimals for millimeters.
    let sizeMillimeters = {
      width: Math.round(pageSizeInches.width * 25.4 * 10) / 10,
      height: Math.round(pageSizeInches.height * 25.4 * 10) / 10,
    };

    let pageName =
      tw2018func_getPageName(sizeInches, isPortrait, US_PAGE_NAMES) ||
      tw2018func_getPageName(sizeMillimeters, isPortrait, METRIC_PAGE_NAMES);

    if (
      !pageName &&
      !(
        Number.isInteger(sizeMillimeters.width) &&
        Number.isInteger(sizeMillimeters.height)
      )
    ) {
      // Attempt to improve the page name detection by falling back to fuzzy
      // matching of the metric dimensions, to account for e.g. rounding errors
      // and/or PDF files that define the page sizes in an imprecise manner.
      const exactMillimeters = {
        width: pageSizeInches.width * 25.4,
        height: pageSizeInches.height * 25.4,
      };
      const intMillimeters = {
        width: Math.round(sizeMillimeters.width),
        height: Math.round(sizeMillimeters.height),
      };

      // Try to avoid false positives, by only considering "small" differences.
      if (
        Math.abs(exactMillimeters.width - intMillimeters.width) < 0.1 &&
        Math.abs(exactMillimeters.height - intMillimeters.height) < 0.1
      ) {
        pageName = tw2018func_getPageName(
          intMillimeters,
          isPortrait,
          METRIC_PAGE_NAMES
        );
        if (pageName) {
          // Update *both* sizes, computed above, to ensure that the displayed
          // dimensions always correspond to the detected page name.
          sizeInches = {
            width: Math.round((intMillimeters.width / 25.4) * 100) / 100,
            height: Math.round((intMillimeters.height / 25.4) * 100) / 100,
          };
          sizeMillimeters = intMillimeters;
        }
      }
    }

    if (pageName) {
      return pageName.toLowerCase();
      /* pageName = this.l10n.get(
        "document_properties_page_size_name_" + pageName.toLowerCase(),
        null,
        pageName
      ); */
    }

    return Promise.all([
      this._isNonMetricLocale ? sizeInches : sizeMillimeters,
      this._isNonMetricLocale ? "in" : "mm",
      pageName,
      isPortrait ? "portrait" : "landscape",
    ]).then(([{ width, height }, unit, name, orientation]) => {
      if (name) {
        return `${width.toLocaleString()} × ${height.toLocaleString()} ${unit} (${name}, ${orientation})`;
      }

      return `${width.toLocaleString()} × ${height.toLocaleString()} ${unit} (${orientation})`;
    });
  }

  /**
   * @private
   */
  _parseDate(inputDate) {
    if (!inputDate) {
      return undefined;
    }
    // This is implemented according to the PDF specification, but note that
    // Adobe Reader doesn't handle changing the date to universal time
    // and doesn't use the user's time zone (they're effectively ignoring
    // the HH' and mm' parts of the date string).
    let dateToParse = inputDate;

    // Remove the D: prefix if it is available.
    if (dateToParse.substring(0, 2) === "D:") {
      dateToParse = dateToParse.substring(2);
    }

    // Get all elements from the PDF date string.
    // JavaScript's `Date` object expects the month to be between
    // 0 and 11 instead of 1 and 12, so we're correcting for this.
    const year = parseInt(dateToParse.substring(0, 4), 10);
    const month = parseInt(dateToParse.substring(4, 6), 10) - 1;
    const day = parseInt(dateToParse.substring(6, 8), 10);
    let hours = parseInt(dateToParse.substring(8, 10), 10);
    let minutes = parseInt(dateToParse.substring(10, 12), 10);
    const seconds = parseInt(dateToParse.substring(12, 14), 10);
    const utRel = dateToParse.substring(14, 15);
    const offsetHours = parseInt(dateToParse.substring(15, 17), 10);
    const offsetMinutes = parseInt(dateToParse.substring(18, 20), 10);

    // As per spec, utRel = 'Z' means equal to universal time.
    // The other cases ('-' and '+') have to be handled here.
    if (utRel === "-") {
      hours += offsetHours;
      minutes += offsetMinutes;
    } else if (utRel === "+") {
      hours -= offsetHours;
      minutes -= offsetMinutes;
    }

    // Return the new date format from the user's locale.
    const date = new Date(Date.UTC(year, month, day, hours, minutes, seconds));
    const dateString = date.toLocaleDateString();
    const timeString = date.toLocaleTimeString();
    return `${dateString}, ${timeString}`;

    /* return this.l10n.get(
      "document_properties_date_string",
      { date: dateString, time: timeString },
      "{{date}}, {{time}}"
    ); */
  }
}

export { PDFDocumentProperties };
